export default function PerformanceSection() {
  const metrics = [
    { label: 'Approach Stability', value: 94, color: '#00FFD1' },
    { label: 'Airspeed Control', value: 96, color: '#00A3E0' },
    { label: 'Radio Communication', value: 89, color: '#B4FF39' },
    { label: 'Pattern Geometry', value: 91, color: '#00FFD1' }
  ];

  const flightData = [72, 78, 81, 85, 88, 90, 92, 94];

  return (
    <section className="py-16 sm:py-24 bg-[#08121F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Every flight earns a score.</h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            WingmanFix analyzes your approach, airspeed control, radio discipline, and workload management to create a clear performance score after every flight.
          </p>
        </div>

        <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#0D1B2E] to-[#0A1628] rounded-3xl p-6 sm:p-8 lg:p-12 border border-[#00FFD1]/20 shadow-2xl">
          {/* Gauges Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
            {metrics.map((metric, idx) => (
              <div key={idx} className="text-center">
                <div className="relative w-24 h-24 sm:w-36 sm:h-36 mx-auto mb-3 sm:mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="50%" cy="50%" r="40%" stroke="#1A2942" strokeWidth="12" fill="none" />
                    <circle 
                      cx="50%" cy="50%" r="40%" 
                      stroke={metric.color} 
                      strokeWidth="12" 
                      fill="none"
                      strokeDasharray="376.8" 
                      strokeDashoffset={376.8 - (376.8 * metric.value / 100)}
                      className="transition-all duration-1000"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl sm:text-4xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    </div>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider px-1">{metric.label}</p>
              </div>
            ))}
          </div>

          {/* Trend Graph */}
          <div className="bg-[#050A14] rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-white">Performance Trend</h3>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#B4FF39]"></div>
                <span className="text-xs sm:text-sm text-gray-400">Overall Score</span>
              </div>
            </div>
            
            {/* Wrap chart in horizontal scroll container for mobile */}
            <div className="overflow-x-auto -mx-2 px-2">
              <div className="min-w-[600px]">
                <div className="relative h-48 sm:h-64">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between">
                    {[100, 75, 50, 25, 0].map((val) => (
                      <div key={val} className="flex items-center">
                        <span className="text-xs text-gray-600 w-6 sm:w-8">{val}</span>
                        <div className="flex-1 h-px bg-[#1A2942]"></div>
                      </div>
                    ))}
                  </div>

                  {/* Line chart */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 256" preserveAspectRatio="none">
                    <polyline
                      points={flightData.map((val, idx) => `${(idx * 100) + 50},${256 - (val * 2.4)}`).join(' ')}
                      fill="none"
                      stroke="#B4FF39"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {flightData.map((val, idx) => (
                      <circle
                        key={idx}
                        cx={(idx * 100) + 50}
                        cy={256 - (val * 2.4)}
                        r="5"
                        fill="#B4FF39"
                        className="hover:r-8 transition-all cursor-pointer"
                      />
                    ))}
                  </svg>

                  {/* X-axis labels */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-between px-6 sm:px-8 pt-4">
                    {flightData.map((_, idx) => (
                      <span key={idx} className="text-xs text-gray-600">Flight {idx + 1}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flight Path Replay */}
          <div className="mt-6 sm:mt-8 flex items-center justify-center sm:justify-end gap-3">
            <button className="flex items-center gap-2 bg-[#00A3E0] hover:bg-[#0088BB] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
              <i className="ri-play-circle-line text-lg sm:text-xl"></i>
              <span className="font-medium">Flight Path Replay</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}